import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

interface InactiveUser {
  auth_id: number;
  email: string;
  role: string;
  nome: string | null;
  avatar_url: string | null;
  cidade: string | null;
  estado: string | null;
  registered_at: string;
  last_activity: string;
  days_inactive: number;
}

interface InactiveStats {
  by_period: {
    '30_days': number;
    '60_days': number;
    '90_days': number;
    '180_days': number;
    '365_days': number;
  };
  by_role_30d: { usuarios: number; bandas: number; empresas: number };
  by_role_90d: { usuarios: number; bandas: number; empresas: number };
  by_role_365d: { usuarios: number; bandas: number; empresas: number };
  total_users: number;
}

interface InactiveData {
  inactive_days_filter: number;
  analytics_tracking_since: string | null;
  note: string;
  stats: InactiveStats;
  distribution_by_month: { month: string; count: number; usuarios: number; bandas: number; empresas: number }[];
  users: InactiveUser[];
}

const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'];
const ROLE_COLORS: { [key: string]: string } = {
  usuario: '#4ECDC4',
  banda: '#FF6B6B',
  empresa: '#45B7D1'
};

const ROLE_LABELS: { [key: string]: string } = {
  usuario: 'Usuário',
  banda: 'Banda',
  empresa: 'Empresa'
};

const InactiveUsersSection: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<InactiveData | null>(null);
  const [inactiveDays, setInactiveDays] = useState(30);
  const [roleFilter, setRoleFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInactiveUsers();
  }, [inactiveDays, roleFilter]);

  const fetchInactiveUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/analytics/inactive-users?inactiveDays=${inactiveDays}&limit=100&role=${roleFilter}`);
      setData(response.data.data);
    } catch (err: any) {
      console.error('Erro ao buscar usuários inativos:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return 'N/A';
    }
  };

  const formatRelativeTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { locale: ptBR, addSuffix: true });
    } catch {
      return 'N/A';
    }
  };

  const getInactivityColor = (days: number) => {
    if (days >= 365) return '#dc3545'; // vermelho - crítico
    if (days >= 180) return '#fd7e14'; // laranja - alto risco
    if (days >= 90) return '#ffc107';  // amarelo - atenção
    if (days >= 60) return '#20c997';  // verde claro - moderado
    return '#17a2b8'; // azul - recente
  };

  const getInactivityLabel = (days: number) => {
    if (days >= 365) return 'Crítico';
    if (days >= 180) return 'Alto Risco';
    if (days >= 90) return 'Atenção';
    if (days >= 60) return 'Moderado';
    return 'Recente';
  };

  if (loading && !data) {
    return (
      <div style={styles.section}>
        <div style={styles.loading}>Carregando dados de usuários inativos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.section}>
        <div style={styles.error}>
          <p>Erro: {error}</p>
          <button onClick={fetchInactiveUsers} style={styles.retryButton}>Tentar Novamente</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Preparar dados para gráficos
  const periodChartData = [
    { period: '30 dias', count: data.stats.by_period['30_days'] },
    { period: '60 dias', count: data.stats.by_period['60_days'] },
    { period: '90 dias', count: data.stats.by_period['90_days'] },
    { period: '180 dias', count: data.stats.by_period['180_days'] },
    { period: '1 ano', count: data.stats.by_period['365_days'] }
  ];

  const roleChartData = [
    { name: 'Usuários', value: data.stats.by_role_30d.usuarios, color: ROLE_COLORS.usuario },
    { name: 'Bandas', value: data.stats.by_role_30d.bandas, color: ROLE_COLORS.banda },
    { name: 'Empresas', value: data.stats.by_role_30d.empresas, color: ROLE_COLORS.empresa }
  ].filter(item => item.value > 0);

  return (
    <div style={styles.section}>
      <div style={styles.header}>
        <h2 style={styles.title}>
          <span style={styles.warningIcon}>⚠️</span>
          Usuários Inativos
        </h2>
        <div style={styles.filters}>
          <select
            value={inactiveDays}
            onChange={(e) => setInactiveDays(parseInt(e.target.value))}
            style={styles.select}
          >
            <option value={30}>Sem acesso há 30+ dias</option>
            <option value={60}>Sem acesso há 60+ dias</option>
            <option value={90}>Sem acesso há 90+ dias</option>
            <option value={180}>Sem acesso há 180+ dias</option>
            <option value={365}>Sem acesso há 1+ ano</option>
          </select>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">Todos os tipos</option>
            <option value="usuario">Usuários</option>
            <option value="banda">Bandas</option>
            <option value="empresa">Empresas</option>
          </select>
        </div>
      </div>

      {/* Nota informativa sobre período de rastreamento */}
      {data.analytics_tracking_since && (
        <div style={styles.infoNote}>
          <span style={styles.infoIcon}>ℹ️</span>
          <span>
            <strong>Rastreamento ativo desde: {formatDate(data.analytics_tracking_since)}</strong>
            <br />
            <span style={styles.infoSubtext}>
              {data.note} | Total de {data.stats.total_users} usuários com dados de analytics.
            </span>
          </span>
        </div>
      )}

      {/* Cards de estatísticas */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #dc3545' }}>
          <div style={styles.statLabel}>Inativos há 1+ ano</div>
          <div style={styles.statValue}>{data.stats.by_period['365_days']}</div>
          <div style={styles.statSubLabel}>Crítico - considerar exclusão</div>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #fd7e14' }}>
          <div style={styles.statLabel}>Inativos há 180+ dias</div>
          <div style={styles.statValue}>{data.stats.by_period['180_days']}</div>
          <div style={styles.statSubLabel}>Alto risco de abandono</div>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #ffc107' }}>
          <div style={styles.statLabel}>Inativos há 90+ dias</div>
          <div style={styles.statValue}>{data.stats.by_period['90_days']}</div>
          <div style={styles.statSubLabel}>Requer atenção</div>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #17a2b8' }}>
          <div style={styles.statLabel}>Inativos há 30+ dias</div>
          <div style={styles.statValue}>{data.stats.by_period['30_days']}</div>
          <div style={styles.statSubLabel}>Engajamento baixo</div>
        </div>
      </div>

      {/* Gráficos lado a lado */}
      <div style={styles.chartsRow}>
        {/* Gráfico de barras - por período */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Inativos por Período</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={periodChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" name="Usuários" fill="#FF6B6B" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico de pizza - por tipo */}
        <div style={styles.chartCard}>
          <h3 style={styles.chartTitle}>Distribuição por Tipo (30+ dias)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={roleChartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={(entry) => `${entry.name}: ${entry.value}`}
              >
                {roleChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tabela de usuários inativos */}
      <div style={styles.tableContainer}>
        <div style={styles.tableHeader}>
          <h3 style={styles.tableTitle}>
            Lista de Inativos ({data.users.length} resultados)
          </h3>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Usuário</th>
              <th style={styles.th}>Tipo</th>
              <th style={styles.th}>Localização</th>
              <th style={styles.th}>Cadastro</th>
              <th style={styles.th}>Último Acesso</th>
              <th style={styles.th}>Dias Inativo</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((user, index) => (
              <tr key={user.auth_id} style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                <td style={styles.td}>
                  <div style={styles.userCell}>
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt="" style={styles.avatar} />
                    ) : (
                      <div style={styles.avatarPlaceholder}>
                        {(user.nome || user.email).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div style={styles.userName}>{user.nome || 'Sem nome'}</div>
                      <div style={styles.userEmail}>{user.email}</div>
                    </div>
                  </div>
                </td>
                <td style={styles.td}>
                  <span style={{ ...styles.roleBadge, backgroundColor: ROLE_COLORS[user.role] }}>
                    {ROLE_LABELS[user.role] || user.role}
                  </span>
                </td>
                <td style={styles.td}>
                  {user.cidade && user.estado ? `${user.cidade}, ${user.estado}` : 'N/A'}
                </td>
                <td style={styles.td}>{formatDate(user.registered_at)}</td>
                <td style={styles.td}>
                  <div>{formatDate(user.last_activity)}</div>
                  <div style={styles.relativeTime}>{formatRelativeTime(user.last_activity)}</div>
                </td>
                <td style={styles.td}>
                  <span style={{ ...styles.daysInactive, color: getInactivityColor(user.days_inactive) }}>
                    {user.days_inactive} dias
                  </span>
                </td>
                <td style={styles.td}>
                  <span style={{ ...styles.statusBadge, backgroundColor: getInactivityColor(user.days_inactive) }}>
                    {getInactivityLabel(user.days_inactive)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.users.length === 0 && (
          <div style={styles.noData}>
            Nenhum usuário inativo encontrado com os filtros selecionados.
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  section: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '24px',
    marginTop: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  warningIcon: {
    fontSize: '24px',
  },
  filters: {
    display: 'flex',
    gap: '12px',
  },
  select: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    minWidth: '180px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
  },
  statLabel: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '8px',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#333',
  },
  statSubLabel: {
    fontSize: '11px',
    color: '#999',
    marginTop: '8px',
  },
  chartsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    marginBottom: '24px',
  },
  chartCard: {
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
  },
  chartTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '16px',
    margin: '0 0 16px 0',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  tableTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    margin: 0,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    textAlign: 'left',
    padding: '14px 12px',
    borderBottom: '2px solid #e9ecef',
    backgroundColor: '#f8f9fa',
    fontWeight: '600',
    color: '#495057',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '14px 12px',
    borderBottom: '1px solid #e9ecef',
    verticalAlign: 'middle',
  },
  trEven: {
    backgroundColor: '#fff',
  },
  trOdd: {
    backgroundColor: '#f8f9fa',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  avatarPlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#dee2e6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    color: '#495057',
    fontSize: '16px',
  },
  userName: {
    fontWeight: '500',
    color: '#333',
  },
  userEmail: {
    fontSize: '12px',
    color: '#6c757d',
  },
  roleBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '500',
  },
  relativeTime: {
    fontSize: '11px',
    color: '#6c757d',
    marginTop: '2px',
  },
  daysInactive: {
    fontWeight: '600',
    fontSize: '15px',
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '11px',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  noData: {
    padding: '40px',
    textAlign: 'center',
    color: '#6c757d',
    fontSize: '15px',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#6c757d',
    fontSize: '16px',
  },
  error: {
    padding: '40px',
    textAlign: 'center',
    color: '#dc3545',
  },
  retryButton: {
    marginTop: '16px',
    padding: '10px 24px',
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  infoNote: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    backgroundColor: '#e3f2fd',
    padding: '16px 20px',
    borderRadius: '8px',
    marginBottom: '24px',
    border: '1px solid #90caf9',
  },
  infoIcon: {
    fontSize: '20px',
  },
  infoSubtext: {
    fontSize: '13px',
    color: '#555',
    marginTop: '4px',
  },
};

export default InactiveUsersSection;
